/**
 * Extracts a human-readable error message from an Axios error.
 *
 * Handles all error shapes returned by this API:
 *   { error: "..." }                          — most controller error responses
 *   { message: "..." }                        — auth and some other endpoints
 *   { title: "...", errors: { F: ["msg"] } }  — ASP.NET Core model validation
 *   { title: "..." }                          — ASP.NET Core problem details
 *   plain string body                         — uncommon
 *   no response (network error)               — err.message fallback
 *
 * @param {unknown} err - The caught error (typically an Axios error)
 * @param {string} [fallback] - Message to return when nothing else can be extracted
 * @returns {string}
 */
export function parseApiError(err, fallback = 'An unexpected error occurred.') {
  const data = err?.response?.data;

  if (!data) return err?.message || fallback;

  if (typeof data === 'string') return data;

  // { error: "..." } — our standard controller error shape
  if (typeof data.error === 'string') return data.error;

  // { message: "..." } — auth / other endpoints
  if (typeof data.message === 'string') return data.message;

  // ASP.NET Core model validation: { title, errors: { Field: ["msg", ...] } }
  if (data.errors && typeof data.errors === 'object') {
    const fieldMessages = Object.values(data.errors).flat().filter(Boolean);
    if (fieldMessages.length > 0) return fieldMessages.join(' ');
  }

  // ASP.NET Core problem details: { title }
  if (typeof data.title === 'string') return data.title;

  return err?.message || fallback;
}
